<template>
  <div class="form-field form-field-text">
    <label :for="field.name">{{ label }}</label>
    <div class="input-wrapper" :class="field.type">
      <input
        v-if="field.name !== 'zip'"
        :id="field.name"
        v-model="fieldValue"
        :name="field.name"
        :type="displayType"
        :autocomplete="field.autocomplete"
        :readonly="field.readonly || false"
        :tabindex="field.readonly ? -1 : 0"
        @focus="blured = false"
        @blur="blured = true"
      />
      <input
        v-else
        :id="field.name"
        v-model="fieldValue"
        :name="field.name"
        autocomplete="postal-code"
        :readonly="field.readonly || false"
        :tabindex="field.readonly ? -1 : 0"
        pattern="[0-9]{5}"
        type="text"
        @focus="blured = false"
        @blur="blured = true"
      />
      <button
        tabindex="-1"
        type="button"
        class="show-hide-password"
        :aria-label="ariaLabelDisplayPassword"
        @click="() => (displayAsText = !displayAsText)"
      >
        <transition name="scale" mode="out-in">
          <icon
            v-if="displayType !== 'password'"
            key="open"
            name="eyeOpen"
          ></icon>
          <icon v-else key="close" name="eyeClose"></icon>
        </transition>
      </button>
    </div>
    <FormErrorMessages :messages="feedback"></FormErrorMessages>
    <aside v-if="field.type === 'password' && field?.config?.forgetPaswwordUrl">
      <nuxt-link
        v-if="field.type === 'password'"
        class="password-forget"
        :href="field?.config?.forgetPaswwordUrl"
      >
        {{ field?.config?.forgetPaswwordLabel }}
      </nuxt-link>
    </aside>
  </div>
</template>
<script setup lang="ts">
interface Props {
  field: FormField;
}
const props = defineProps({
  field: null
});

const {
  formFieldsData: { memberValues },
  checkValidatedFormOnMounted,
} = inject(INJECT_FORM_PAGE);

const blured = ref<boolean>(false);

const displayAsText = ref<boolean>(false);

const ariaLabelDisplayPassword = computed(() => {
  if (displayType.value === 'password') {
    return 'Afficher le mot de passe';
  } else {
    return 'Masquer le mot de passe';
  }
});

const fieldValue = computed({
  get() {
    return memberValues.value[props.field.name].value;
  },
  set(value) {
    if (props.field.type === 'email') {
      value = value.toLowerCase();
    }
    memberValues.value[props.field.name].value = value;
  },
});

const displayType = computed(() => {
  if (props.field.type !== 'password') return props.field.type;
  return displayAsText.value ? 'text' : props.field.type;
});

const label = computed(() => {
  return isFieldRequired(props.field)
    ? `${props.field.label}*`
    : props.field.label;
});

const { messages } = useFormFieldValidation(props.field, memberValues);

const feedback = computed(() => {
  let feedbackMessages = [''];
  if (blured.value && messages.value.length > 0) {
    feedbackMessages = [...messages.value];
  }
  return feedbackMessages;
});

onMounted(() => {
  if (checkValidatedFormOnMounted.value) blured.value = true;
});
</script>
<style lang="scss" scoped>
.input-wrapper {
  position: relative;
  button.show-hide-password {
    display: none;
  }
  input {
    width: 100%;
  }
  input[readonly] {
    color: var(--color_neutral_500);
    pointer-events: none;
  }
  &.password {
    input {
      padding-right: 5rem;
    }
    button.show-hide-password {
      display: flex;
      position: absolute;
      top: 0;
      right: 0;
      align-items: center;
      justify-content: center;
      width: 5rem;
      height: 100%;
      color: var(--color_neutral_200);
    }
  }
}
aside {
  padding: 0.6rem 1rem 0;
}
.password-forget {
  position: relative;
  color: var(--color_neutral_600);
  text-align: start;
  justify-self: flex-start;

  @include font('s', '400');

  &::after,
  &::before {
    content: '';
    position: absolute;
    bottom: -0.2rem;
    left: 0;
    width: 100%;
    height: 0.2rem;
    transform: scaleX(1);
    transform-origin: 100%;
    transition: transform 300ms $ease-in-quad 200ms;
    background-color: var(--color_neutral_600);
  }
  &::before {
    transform: scaleX(0);
    transform-origin: 0%;
    transition: transform 300ms $ease-out-quad;
  }
  &:hover,
  &:focus {
    &::after {
      transform: scaleX(0);
      transform-origin: 0%;
      transition: transform 300ms $ease-out-quad;
    }
    &::before {
      transform: scaleX(1);
      transform-origin: 100%;
      transition: transform 300ms $ease-in-quad 200ms;
    }
  }
}
</style>
